import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomDatePicker from "common/CustomDatePicker";
import CustomSelect from "components/Common/custom-select";
import TableContainer from "components/Common/TableContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation, useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";
import { fetchAttendanceConsolidatedReport, fetchCategories, fetchDepartments, fetchEmployeeDropdown, fetchEmployeeDropdownAutoList, resetAttendanceReport } from "store/actions";
import Loader from "common/pageLoader";
import { getAttendanceSalaryDate } from "common/attendanceSalaryDate";

const AttendanceReportConsolidate = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prev) => !prev);
  const {
    employeeReport,
    categories,
    departments,
    dropdownAutoList,
    dropdownData,
    loading,
    error,
    success
  } = useSelector((state) => ({
    employeeReport: state.AttendanceReportReducer?.employeeReport ?? [],
    categories: state.CategoryMaster?.categories ?? [],
    departments: state.DepartmentMaster?.departments ?? [],
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],
    dropdownData: state.EmployeeDetailsReducer?.dropdownData,

    loading: state.AttendanceReportReducer?.loading,
    error: state.AttendanceReportReducer?.error,
    success: state.AttendanceReportReducer?.success,
  }));

  const { empment_type } = dropdownData;
 
  useEffect(() => {
      if (authDetails) {
        getAttendanceSalaryDate(authDetails.unit_id).then(response => {
          if (response.status === "success" && response.data.length > 0) {
            const data = response.data[0];
            formik.setFieldValue("fromDate", data.from_date.split('/').reverse().join('-'));
            formik.setFieldValue("toDate", data.to_date.split('/').reverse().join('-'));
          }
        });
      }
    }, [authDetails]);
    
  useEffect(() => {
    dispatch(resetAttendanceReport())
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchCategories(unitId));
    dispatch(fetchDepartments(unitId));
    dispatch(fetchEmployeeDropdownAutoList(unitId))
    dispatch(fetchEmployeeDropdown(unitId))
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      fromDate: "",
      toDate: "",
      category: "",
      department: "",
      employee: "",
      employeeType: "",
    },
    validationSchema: Yup.object({
      fromDate: Yup.date().nullable()
        .required("From Date is required"),

      toDate: Yup.date().nullable()
        .required("To Date is required")
        .min(
          Yup.ref("fromDate"),
          "To Date must be greater than or equal to From Date"
        ),
    }),
    onSubmit: (values) => {
      const formData = new FormData();

      formData.append("from_date", values.fromDate);
      formData.append("to_date", values.toDate);

      formData.append("emp_cat", values.category || 0);
      formData.append("emp_dep", values.department || 0);
      formData.append("emp_code", values.employee || 0);
      formData.append("emp_type", values.employeeType || 0);

      formData.append("unit_id", authDetails?.unit_id);
      formData.append("created_by", authDetails?.user_id);

      dispatch(fetchAttendanceConsolidatedReport(formData));
    },
  });

  // Static / always visible columns
  const staticColumns = [
    { Header: "S.No", Cell: ({ row }) => employeeReport.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
    { Header: "Code", accessor: "emp_no" },
    { Header: "Name", accessor: "emp_name" },
    { Header: "Department", accessor: "department" },
    { Header: "Designation", accessor: "designation" },
  ];

  // Generate dynamic date columns from API response keys
  const generateAttendanceColumns = (data = []) => {
    if (!data.length) return staticColumns;

    const firstRow = data[0];

    const dynamicDateColumns = Object.keys(firstRow)
      .filter(
        key =>
          key.match(/^\d{2}\.\d{2}\.\d{2}$/) // Matches 01.11.25 format
      )
      .map(dateKey => ({
        Header: dateKey,
        accessor: String(dateKey),
        Cell: ({ row }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              lineHeight: "1.4",
              width: "250px"
            }}
          >
            {row.original?.[dateKey] ?? "-"}
          </div>
        ),
        applyHeaderClass: true
      }));

    // Add last fixed summary columns
    const summaryColumns = [
      { Header: "Present", accessor: "present", className: "text-center" },
      { Header: "OD", accessor: "od", className: "text-center" },
      { Header: "Holiday", accessor: "holiday", className: "text-center" },
      { Header: "Week Off", accessor: "week_off", className: "text-center" },
      { Header: "Absent", accessor: "absent", className: "text-center" },
      { Header: "Leave", accessor: "leave", className: "text-center" },
      // { Header: "Shift Allocated Days", accessor: "shift_allocated_days", className: "text-center" },
      { Header: "Total Days", accessor: "total_days", className: "text-center" },
    //  { Header: "DOJ", accessor: "date_of_joining", className: "text-center" },
      // { Header: "Extra Hours", accessor: "extra_hours", className: "text-center", Cell: ({ value }) => value ?? "-" },
      // { Header: "Late", accessor: "shift_late", className: "text-center", Cell: ({ value }) => value ?? "-" },
      //{ Header: "Casual Leave", accessor: "casual_Leave", className: "text-center", Cell: ({ value }) => value ?? "-" },
      //{ Header: "Loss of Pay", accessor: "loss_of_pay", className: "text-center", Cell: ({ value }) => value ?? "-" },
      // { Header: "Permission", accessor: "total_permission", className: "text-center", Cell: ({ value }) => value ?? "-" },
    ];

    return [...staticColumns, ...dynamicDateColumns, ...summaryColumns];
  };


  const columns = useMemo(() => generateAttendanceColumns(employeeReport), [employeeReport]);


  const handleReset = () => {
    formik.resetForm();
    dispatch(resetAttendanceReport());
  };

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Attendance" breadcrumbItem="Consolidate Report" />
        <Card>
          <CardBody>
            <form onSubmit={formik.handleSubmit}>
              <Row className="gy-3 ">
                <Col md={3}>
                  <Label>Category</Label>
                  <CustomSelect
                    id="category"
                    name="category"
                    options={categories.map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))}
                    value={
                      categories
                        .map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))
                        .find(cat => cat.value === formik.values.category) || null
                    }
                    onChange={val => formik.setFieldValue('category', val ? val.value : '')}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.category && formik.errors.category ? 'is-invalid' : ''}`}
                  />
                </Col>

                <Col md={3}>
                  <Label>Department</Label>
                  <CustomSelect
                    id="department"
                    name="department"
                    options={departments.map(dept => ({ value: dept.departmentId, label: dept.departmentname }))}
                    value={
                      departments
                        .map(dept => ({ value: dept.departmentId, label: dept.departmentname }))
                        .find(dept => dept.value === formik.values.department) || null
                    }
                    onChange={val => formik.setFieldValue('department', val ? val.value : '')}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.department && formik.errors.department ? 'is-invalid' : ''}`}
                  />
                </Col>
                <Col md={3}>
                  <Label>Employee Type</Label>
                  <CustomSelect
                    id="employeeType"
                    name="employeeType"
                    options={(empment_type || []).map(type => ({
                      value: type.emptype_id,
                      label: type.emp_type
                    }))}

                    value={
                      (empment_type || [])
                        .map(type => ({
                          value: type.emptype_id,
                          label: type.emp_type
                        }))
                        .find(type => type.value === formik.values.employeeType) || null
                    }

                    onChange={(selected) =>
                      formik.setFieldValue("employeeType", selected ? selected.value : "")
                    }

                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.employeeType && formik.errors.employeeType ? "is-invalid" : ""
                      }`}
                  />
                </Col>
                <Col md={3}>
                  <Label>
                    Employee
                  </Label>
                  <CustomSelect
                    id="employee"
                    name="employee"
                    options={dropdownAutoList.map(emp => ({
                      value: String(emp.emp_id),
                      label: emp.emp_no
                    }))}

                    // UI needs the object, so find matching option
                    value={
                      dropdownAutoList
                        .map(emp => ({
                          value: String(emp.emp_id),
                          label: emp.emp_no
                        }))
                        .find(option => option.value === formik.values.employee) || null
                    }

                    // Form Data: store string only
                    onChange={(selected) =>
                      formik.setFieldValue("employee", selected ? selected.value : "")
                    }
                  />
                  {formik.touched.employee && formik.errors.employee && (
                    <div className="text-danger">
                      {formik.errors.employee}
                    </div>
                  )}
                </Col>
                <Col md={3}>
                  <Label>From Date <span className="text-danger">*</span></Label>
                  <CustomDatePicker
                    name="fromDate"
                    value={formik.values.fromDate}
                    onChange={(val) => formik.setFieldValue("fromDate", val)}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.fromDate && formik.errors.fromDate && (
                    <div className="text-danger">{formik.errors.fromDate}</div>
                  )}
                </Col>

                <Col md={3}>
                  <Label>To Date <span className="text-danger">*</span></Label>
                  <CustomDatePicker
                    name="toDate"
                    value={formik.values.toDate}
                    onChange={(val) => formik.setFieldValue("toDate", val)}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.toDate && formik.errors.toDate && (
                    <div className="text-danger">{formik.errors.toDate}</div>
                  )}
                </Col>


                <Col className="d-flex mob-center align-items-center justify-content-end">
                  <Button type="submit" className="btn button-search me-2 mt-2">
                    <i className="fas fa-search me-1" /> Search
                  </Button>
                  <Button type="button" className="btn button-cancel me-2 mt-2" onClick={handleReset}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                  {employeeReport && employeeReport.length > 0 && (
                    <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                      <DropdownToggle color="primary" className="btn button-export mt-2">
                        <i className="fas fa-file-export me-1" /> Export
                      </DropdownToggle>

                      <DropdownMenu>
                        <DropdownItem onClick={() => exportEmployeeToExcel(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "Consolidated Report")}>
                          <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                        </DropdownItem>

                        <DropdownItem onClick={() => exportEmployeeToPDF(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "Consolidated Report", columns)}>
                          <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={employeeReport}
              isGlobalFilter={true}
              customPageSize={10}
              tableSorter={true}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default AttendanceReportConsolidate;
